// 통합 결제 서비스 - Iamport 기반 (동적 금액 로딩)
import { collection, addDoc, updateDoc, doc, getDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { createNotification } from './NotificationSystem';
import { MEMBERSHIP_TIERS, APP_SUBSCRIPTION } from '../config/pricing';

// pricing.js에서 멤버십/구독 가격을 자동 생성
const buildMembershipPrices = () => {
  const prices = {};
  MEMBERSHIP_TIERS.filter(t => t.percent > 0).forEach(tier => {
    prices[`membership-tier-${tier.percent}`] = {
      name: `아이반 기공 멤버십 ${tier.percent}%`,
      price: tier.cost,
      duration: 30,
      description: `월 ${(tier.cost / 10000).toLocaleString()}만원 - 기공료 ${tier.percent}% 할인`,
      billingType: 'exempt',
      billingEntity: 'ivan_lab',
    };
  });
  return prices;
};

// 기본 결제 금액 (pricing.js 기반)
const DEFAULT_SERVICE_PRICES = {
  'lab-advertisement': { name: '기공소 홍보', price: 30000, duration: 30, description: '30일간 기공소 홍보 서비스' },
  'seminar': { name: '세미나 등록', price: 50000, duration: 60, description: '60일간 세미나 홍보' },
  'job-posting': { name: '구인공고 등록', price: 20000, duration: 30, description: '30일간 구인공고 게시' },
  'advertisement': { name: '광고 등록', basic: { price: 50000, duration: 30, description: '베이직 광고 30일' }, standard: { price: 100000, duration: 30, description: '스탠다드 광고 30일' }, premium: { price: 200000, duration: 30, description: '프리미엄 광고 30일' } },
  'new-product': { name: '신제품 등록', price: 30000, duration: 60, description: '60일간 신제품 홍보' },
  'marketplace': { name: '마켓플레이스 수수료', commissionRate: 5, description: '상품 판매 시 매출의 일정 비율' },
  'matching-basic': { name: '매칭 베이직', price: 30000, duration: 30, description: '30일간 치과 매칭 서비스' },
  'matching-premium': { name: '매칭 프리미엄', price: 50000, duration: 30, description: '30일간 프리미엄 매칭 서비스' },
  ...buildMembershipPrices(),
  'app-subscription': { name: '덴탈클러스터 앱 이용료', price: APP_SUBSCRIPTION.totalAmount, duration: 30, description: `월 ${APP_SUBSCRIPTION.monthlyFee.toLocaleString()}원 + VAT ${APP_SUBSCRIPTION.vatAmount.toLocaleString()}원`, billingType: APP_SUBSCRIPTION.billingType, billingEntity: APP_SUBSCRIPTION.billingEntity },
};

let cachedPrices = null;
let lastFetchTime = null;
const CACHE_DURATION = 5 * 60 * 1000;

// Firestore에서 결제 금액 설정 로드
export const loadServicePrices = async (forceRefresh = false) => {
  try {
    const now = Date.now();
    if (!forceRefresh && cachedPrices && lastFetchTime && (now - lastFetchTime < CACHE_DURATION)) { console.log('✅ 캐시된 결제 설정 사용'); return cachedPrices; }
    console.log('🔄 Firestore에서 결제 설정 로드 중...');
    const settingsDoc = await getDoc(doc(db, 'systemSettings', 'paymentPrices'));
    if (settingsDoc.exists()) { cachedPrices = settingsDoc.data().prices || DEFAULT_SERVICE_PRICES; lastFetchTime = now; console.log('✅ 결제 설정 로드 완료:', cachedPrices); return cachedPrices; }
    else { console.log('⚠️ 설정 문서 없음 - 기본값 사용'); cachedPrices = DEFAULT_SERVICE_PRICES; lastFetchTime = now; return DEFAULT_SERVICE_PRICES; }
  } catch (error) { console.error('❌ 결제 설정 로드 실패 - 기본값 사용:', error); return DEFAULT_SERVICE_PRICES; }
};

// 특정 서비스의 가격 정보 가져오기
export const getServicePrice = async (serviceType, tier = null) => { const prices = await loadServicePrices(); return (serviceType === 'advertisement' && tier) ? prices[serviceType][tier] : prices[serviceType]; };

// 아임포트 스크립트 로드
export const loadIamportScript = () => {
  return new Promise((resolve, reject) => {
    if (window.IMP) { resolve(true); return; }
    const script = document.createElement('script'); script.src = 'https://cdn.iamport.kr/v1/iamport.js'; script.async = true;
    script.onload = () => resolve(true); script.onerror = () => reject(new Error('아임포트 스크립트 로드 실패'));
    document.head.appendChild(script);
  });
};

// 아임포트 초기화
export const initializeIamport = () => { if (window.IMP) window.IMP.init(process.env.REACT_APP_IAMPORT_MERCHANT_ID || 'imp00000000'); };

// 주문번호 생성
export const generateOrderNumber = (serviceType) => {
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
  const timeStr = now.getTime().toString().slice(-6);
  const randomStr = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${serviceType.toUpperCase().substring(0, 3)}${dateStr}${timeStr}${randomStr}`;
};

// 통합 결제 요청
export const requestUnifiedPayment = async ({ serviceType, tier = null, amount, serviceName, buyerName, buyerEmail, buyerPhone, paymentMethod = 'card', additionalData = {} }) => {
  return new Promise((resolve, reject) => {
    if (!window.IMP) { reject(new Error('아임포트가 초기화되지 않았습니다.')); return; }
    const orderNumber = generateOrderNumber(serviceType);
    const paymentData = { pg: 'html5_inicis', pay_method: paymentMethod, merchant_uid: orderNumber, name: serviceName, amount: amount, buyer_email: buyerEmail, buyer_name: buyerName, buyer_tel: buyerPhone, m_redirect_url: `${window.location.origin}/payment/callback`, ...(paymentMethod === 'vbank' && additionalData.vbankHolder && { vbank_holder: additionalData.vbankHolder }), custom_data: JSON.stringify({ serviceType: serviceType, tier: tier, ...additionalData }) };
    console.log('🔵 결제 요청:', paymentData);
    window.IMP.request_pay(paymentData, (response) => {
      console.log('🔵 결제 응답:', response);
      if (response.success) { resolve({ success: true, imp_uid: response.imp_uid, merchant_uid: response.merchant_uid, paid_amount: response.paid_amount, apply_num: response.apply_num, pg_tid: response.pg_tid, receipt_url: response.receipt_url, orderNumber: orderNumber }); }
      else { reject({ success: false, error_code: response.error_code, error_msg: response.error_msg }); }
    });
  });
};

// 결제 완료 후 서비스별 처리
export const createServicePayment = async ({ userId, userInfo, serviceType, tier = null, payment, contentId = null, contentData = {} }) => {
  try {
    if (userInfo.companyId) throw new Error('직원 계정은 결제할 수 없습니다. 업체 대표에게 문의하세요.');
    console.log('📝 결제 데이터 생성:', serviceType);
    const serviceInfo = await getServicePrice(serviceType, tier);
    if (!serviceInfo) throw new Error(`서비스 타입 "${serviceType}"의 가격 정보를 찾을 수 없습니다.`);
    const expiryDate = new Date(); expiryDate.setDate(expiryDate.getDate() + serviceInfo.duration);
    const paymentData = { userId, userName: userInfo.name || userInfo.email, userEmail: userInfo.email, userPhone: userInfo.phone || '', serviceType, serviceName: serviceInfo.name, tier, orderNumber: payment.orderNumber, imp_uid: payment.imp_uid, amount: payment.paid_amount, duration: serviceInfo.duration, expiryDate, contentId, contentData, status: 'pending', createdAt: serverTimestamp(), updatedAt: serverTimestamp() };
    const docRef = await addDoc(collection(db, 'servicePayments'), paymentData);
    console.log('✅ 결제 데이터 생성 완료:', docRef.id);

    // 구독/멤버십 결제는 subscriptionPayments에도 동기 기록 (BillingOverview/InvoiceGenerator 연동)
    if (serviceType === 'app-subscription' || serviceType.startsWith('membership-tier-')) {
      const billingType = serviceType === 'app-subscription' ? 'taxed' : 'exempt';
      const billingEntity = serviceType === 'app-subscription' ? 'dentalcluster' : 'ivan_lab';
      const subscriptionType = serviceType === 'app-subscription' ? 'app_usage' : 'membership';
      await addDoc(collection(db, 'subscriptionPayments'), {
        userId,
        subscriptionType,
        billingEntity,
        billingType,
        amount: payment.paid_amount,
        status: 'completed',
        paidAt: serverTimestamp(),
        pgPaymentId: payment.imp_uid,
        pgProvider: 'html5_inicis',
        orderNumber: payment.orderNumber,
        servicePaymentId: docRef.id,
        source: 'web',
        createdAt: serverTimestamp(),
      });
      console.log('✅ subscriptionPayments 동기 기록 완료');
    }

    await notifyAdminsForApproval(serviceType, docRef.id, userInfo);
    return { success: true, paymentId: docRef.id, orderNumber: payment.orderNumber, expiryDate };
  } catch (error) { console.error('❌ 결제 데이터 생성 실패:', error); throw error; }
};

// 관리자에게 승인 요청 알림
const notifyAdminsForApproval = async (serviceType, paymentId, userInfo) => {
  try {
    const adminsSnapshot = await getDocs(query(collection(db, 'users'), where('isAdmin', '==', true)));
    const serviceNames = { 'lab-advertisement': '기공소 홍보', 'seminar': '세미나', 'job-posting': '구인공고', 'advertisement': '광고', 'new-product': '신제품' };
    for (const adminDoc of adminsSnapshot.docs) { await createNotification({ recipientId: adminDoc.id, type: 'payment_approval_request', title: `새로운 ${serviceNames[serviceType]} 승인 요청`, message: `${userInfo.name || userInfo.email}님이 ${serviceNames[serviceType]} 결제를 완료했습니다. 승인이 필요합니다.`, metadata: { paymentId }, link: `/admin/${serviceType}-approval` }); }
  } catch (error) { console.error('⚠️ 관리자 알림 전송 실패:', error); }
};

// 관리자 승인 처리
export const approvePayment = async (paymentId, adminId) => {
  try {
    const paymentRef = doc(db, 'servicePayments', paymentId);
    const paymentDoc = await getDoc(paymentRef);
    if (!paymentDoc.exists()) throw new Error('결제 정보를 찾을 수 없습니다.');
    const paymentData = paymentDoc.data();
    // 멱등성: 이미 처리된 건은 재실행 금지 (관리자 여러 명 동시 처리·더블클릭 시 알림/활성화 2중 실행 방지)
    if (paymentData.status === 'approved') throw new Error('이미 승인 처리된 결제입니다.');
    if (paymentData.status === 'rejected') throw new Error('이미 반려 처리된 결제입니다.');
    await updateDoc(paymentRef, { status: 'approved', approvedBy: adminId, approvedAt: serverTimestamp() });
    if (paymentData.contentId) await activateService(paymentData);
    await createNotification({ recipientId: paymentData.userId, type: 'payment_approved', title: `${paymentData.serviceName} 승인 완료`, message: `${paymentData.serviceName} 신청이 승인되었습니다. 서비스가 활성화되었습니다.`, metadata: { paymentId }, link: getServiceLink(paymentData.serviceType) });
    console.log('✅ 결제 승인 완료:', paymentId);
    return { success: true };
  } catch (error) { console.error('❌ 결제 승인 실패:', error); throw error; }
};

// 관리자 반려 처리 (자동 환불)
export const rejectPayment = async (paymentId, adminId, reason) => {
  try {
    const paymentRef = doc(db, 'servicePayments', paymentId);
    const paymentDoc = await getDoc(paymentRef);
    if (!paymentDoc.exists()) throw new Error('결제 정보를 찾을 수 없습니다.');
    const paymentData = paymentDoc.data();
    // 멱등성: 이미 처리된 건은 재실행 금지 (이중 환불·알림 방지)
    if (paymentData.status === 'approved') throw new Error('이미 승인 처리된 결제입니다.');
    if (paymentData.status === 'rejected') throw new Error('이미 반려 처리된 결제입니다.');
    await updateDoc(paymentRef, { status: 'rejected', rejectedBy: adminId, rejectedAt: serverTimestamp(), rejectionReason: reason });
    try {
      await requestRefund(paymentData.imp_uid, paymentData.amount, reason);
      await createNotification({ recipientId: paymentData.userId, type: 'payment_rejected', title: `${paymentData.serviceName} 반려 및 환불 완료`, message: `${paymentData.serviceName} 신청이 반려되었습니다.\n사유: ${reason}\n결제 금액이 환불 처리되었습니다.`, metadata: { paymentId }, link: getServiceLink(paymentData.serviceType) });
      console.log('✅ 결제 반려 및 환불 완료:', paymentId);
      return { success: true, refunded: true };
    } catch (refundError) {
      await createNotification({ recipientId: paymentData.userId, type: 'payment_rejected', title: `${paymentData.serviceName} 반려`, message: `${paymentData.serviceName} 신청이 반려되었습니다.\n사유: ${reason}\n환불은 별도로 처리됩니다.`, metadata: { paymentId }, link: getServiceLink(paymentData.serviceType) });
      console.error('⚠️ 환불 실패 - 수동 처리 필요:', refundError);
      return { success: true, refunded: false, error: refundError.message };
    }
  } catch (error) { console.error('❌ 결제 반려 실패:', error); throw error; }
};

// 마켓플레이스 주문 생성
export const createMarketplaceOrder = async (orderData) => {
  try {
    const orderRef = await addDoc(collection(db, 'marketplaceOrders'), {
      ...orderData,
      createdAt: serverTimestamp(),
      status: orderData.status || 'placed'
    });
    return { success: true, orderId: orderRef.id };
  } catch (error) {
    console.error('마켓플레이스 주문 생성 실패:', error);
    throw new Error('주문 생성에 실패했습니다.');
  }
};

// 환불 요청
const requestRefund = async (imp_uid, amount, reason) => {
  try {
    console.log('💰 환불 요청:', { imp_uid, amount, reason });
    const response = await fetch('/api/payments/refund', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ imp_uid, amount, reason, checksum: amount }) });
    if (!response.ok) throw new Error('백엔드 환불 API 오류');
    const result = await response.json();
    console.log('✅ 환불 완료 (백엔드):', result);
    return { success: true, refunded: true, method: 'backend', result };
  } catch (error) { console.error('❌ 환불 요청 실패:', error); throw new Error(`환불 처리 실패: ${error.message}`); }
};

// 서비스 활성화
const activateService = async (paymentData) => {
  const { serviceType, contentId, expiryDate } = paymentData;
  try {
    const collectionMap = { 'lab-advertisement': 'labAdvertisements', 'seminar': 'seminars', 'job-posting': 'jobPostings', 'advertisement': 'advertisements', 'new-product': 'newProducts' };
    const collectionName = collectionMap[serviceType];
    if (collectionName) {
      const updateData = serviceType === 'lab-advertisement' ? { isActive: true, status: 'active', expiryDate, updatedAt: serverTimestamp() } : { status: 'active', expiryDate, updatedAt: serverTimestamp() };
      await updateDoc(doc(db, collectionName, contentId), updateData);
    } else { console.warn('알 수 없는 서비스 타입:', serviceType); }
  } catch (error) { console.error('서비스 활성화 실패:', error); throw error; }
};

// 서비스별 링크
const getServiceLink = (serviceType) => {
  const links = { 'lab-advertisement': '/lab-advertising', 'seminar': '/seminars', 'job-posting': '/job-board', 'advertisement': '/ad-manager', 'new-product': '/new-products' };
  return links[serviceType] || '/';
};

// 결제 상태 확인
export const checkPaymentStatus = async (userId, serviceType, contentId = null) => {
  try {
    let q = query(collection(db, 'servicePayments'), where('userId', '==', userId), where('serviceType', '==', serviceType), where('status', '==', 'approved'));
    if (contentId) q = query(q, where('contentId', '==', contentId));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return { isPaid: false, isActive: false };
    const payments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const latestPayment = payments.sort((a, b) => b.createdAt?.toDate() - a.createdAt?.toDate())[0];
    const now = new Date();
    const expiryDate = latestPayment.expiryDate?.toDate() || new Date(latestPayment.expiryDate);
    return { isPaid: true, isActive: expiryDate > now, payment: latestPayment, expiryDate };
  } catch (error) { console.error('결제 상태 확인 실패:', error); return { isPaid: false, isActive: false }; }
};

// 마켓플레이스 수수료 계산
export const calculateMarketplaceCommission = async (saleAmount) => {
  try {
    const prices = await loadServicePrices();
    const commissionRate = prices['marketplace'].commissionRate || 5;
    const commission = Math.round(saleAmount * (commissionRate / 100));
    return { saleAmount, commissionRate, commission, sellerReceives: saleAmount - commission };
  } catch (error) {
    console.error('수수료 계산 실패:', error);
    const commission = Math.round(saleAmount * 0.05);
    return { saleAmount, commissionRate: 5, commission, sellerReceives: saleAmount - commission };
  }
};

const UnifiedPaymentService = { loadServicePrices, getServicePrice, loadIamportScript, initializeIamport, generateOrderNumber, requestUnifiedPayment, createServicePayment, createMarketplaceOrder, approvePayment, rejectPayment, checkPaymentStatus, calculateMarketplaceCommission };

export default UnifiedPaymentService;